import React, { type FC } from 'react';
import css from './markdowns.scss';
import { DataHooks } from '../../constants/dataHooks';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { CopyCodeToClipboard } from '../UtilityComponents/CopyCodeToClipboard';
import oneLight from 'react-syntax-highlighter/dist/cjs/styles/prism/one-light';

export interface CodeBlockProps {
  inline?: boolean;
  language: string;
  code: string;
}

export const CodeBlock: FC<CodeBlockProps> = ({
  inline = true,
  language,
  code,
}) => {
  return !inline || !!language ? (
    <div data-hook={DataHooks.CodeBox} className={css.codeBoxContainer}>
      <div className={css.codeBoxWrapper}>
        <HighlightedCodeBox
          language={getResolvedLanguage(language)}
          code={code}
        />
        <div className={css.copyBox}>
          <div className={css.copyCodeToClipboardWrapper}>
            <CopyCodeToClipboard
              origin="markdown-codeblock"
              dataHook={DataHooks.CodeBlockCopyToClipboard}
              valueToCopy={code}
            />
          </div>
        </div>
      </div>
    </div>
  ) : (
    <code data-hook={DataHooks.CodeText} className={css.simpleCode}>
      {code}
    </code>
  );
};

const HighlightedCodeBox: FC<Partial<CodeBlockProps>> = ({
  code,
  language,
}) => (
  <SyntaxHighlighter
    language={language}
    customStyle={codeBlockStyle}
    codeTagProps={codeTagProps}
    style={oneLight}
    data-hook={DataHooks.CodeText}
  >
    {code}
  </SyntaxHighlighter>
);

const codeTagProps = {
  style: {
    backgroundColor: 'transparent',
    fontFamily: 'JetBrains Mono, monospace',
    fontSize:
      'var(--wds-font-size-body-small, var(--wsr-text-font-size-small, 14px))',
    fontWeight:
      'var(--wds-font-weight-regular, var(--wsr-font-weight-regular, 400))',
    lineHeight:
      'var(--wds-font-line-height-body-small, var(--wsr-text-line-height-small, 18px))',
    marginTop: '2px',
  },
};

const codeBlockStyle = {
  letterSpacing: 'var(--wds-font-letter-spacing-0, unset)',
  backgroundColor: 'transparent',
  paddingRight: '18px',
  paddingBottom: '32px',
  paddingTop: '32px',
  paddingLeft: '18px',
  margin: 0,
  overflow: 'auto',
  display: 'flex',
  minWidth: '300px',
};

export const getResolvedLanguage = (languageParam: string = ''): string => {
  const language = /language-(\w+)/.exec(languageParam);
  return language ? language[1] : 'json';
};
